import { DashaPeriod, PlanetName, PlanetPosition } from '../types/kundali';

export const VIMSHOTTARI_ORDER: PlanetName[] = [
  'Ketu',
  'Venus',
  'Sun',
  'Moon',
  'Mars',
  'Rahu',
  'Jupiter',
  'Saturn',
  'Mercury'
];

export const VIMSHOTTARI_YEARS: Record<string, number> = {
  Ketu: 7,
  Venus: 20,
  Sun: 6,
  Moon: 10,
  Mars: 7,
  Rahu: 18,
  Jupiter: 16,
  Saturn: 19,
  Mercury: 17
};

export interface SubDashaItem {
  planet: PlanetName;
  startDate: string;
  endDate: string;
  durationHours?: number;
  durationDays?: number;
  isCurrent: boolean;
  progressPercent: number;
}

export type SookshmaDashaItem = SubDashaItem;
export type PranaDashaItem = SubDashaItem;

export interface PratyantarDashaItem {
  planet: PlanetName;
  startDate: string;
  endDate: string;
  durationDays: number;
  durationMonths: number;
  isCurrent: boolean;
  daysRemaining: number;
  progressPercent: number;
}

export interface DashaHorizonRelationship {
  angle: number; // 1 to 12
  labelEn: string;
  labelHi: string;
  nature: 'harmonious' | 'frictional' | 'neutral' | 'intense';
}

export interface DashaHorizonItem {
  id: string;
  mdPlanet: PlanetName;
  adPlanet: PlanetName;
  startDate: string;
  endDate: string;
  durationYears: number;
  isCurrent: boolean;
  daysRemaining: number;
  progressPercent: number;
  isDashaChidra: boolean; // Last AD of an MD
  isEraShift: boolean;    // First AD of a new MD transitioning from prior
  prevMdPlanet?: PlanetName;
  ageStart?: number;
  ageEnd?: number;
  relationshipWithMd: DashaHorizonRelationship;
  pratyantardashas: PratyantarDashaItem[];
}

/**
 * Calculates the 9 classical Pratyantardashas inside a given Antardasha window.
 * Uses exact proportional milliseconds to prevent boundary drift.
 */
export function calculatePratyantardashas(
  adPlanet: PlanetName,
  adStartDate: string | Date,
  adEndDate: string | Date
): PratyantarDashaItem[] {
  const startMs = new Date(adStartDate).getTime();
  const endMs = new Date(adEndDate).getTime();
  const totalMs = Math.max(0, endMs - startMs);

  const startIdx = VIMSHOTTARI_ORDER.indexOf(adPlanet);
  const safeStartIdx = startIdx >= 0 ? startIdx : 0;

  const nowMs = Date.now();
  let runningMs = startMs;
  const pds: PratyantarDashaItem[] = [];

  for (let i = 0; i < 9; i++) {
    const planet = VIMSHOTTARI_ORDER[(safeStartIdx + i) % 9];
    const planetYears = VIMSHOTTARI_YEARS[planet] || 7;
    // Classical proportional allocation: (planetYears / 120) * Total AD duration
    const pdMs = Math.round((planetYears / 120) * totalMs);
    const pdStartMs = runningMs;
    // For the last PD, clamp exactly to endMs to eliminate rounding error
    const pdEndMs = i === 8 ? endMs : Math.min(endMs, pdStartMs + pdMs);
    runningMs = pdEndMs;

    const isCurrent = nowMs >= pdStartMs && nowMs <= pdEndMs;
    const daysRemaining = Math.max(0, Math.ceil((pdEndMs - nowMs) / (1000 * 60 * 60 * 24)));
    const durationDays = Math.max(1, Math.round((pdEndMs - pdStartMs) / (1000 * 60 * 60 * 24)));
    const durationMonths = Number((durationDays / 30.4375).toFixed(1));

    let progressPercent = 0;
    if (nowMs > pdEndMs) {
      progressPercent = 100;
    } else if (nowMs >= pdStartMs) {
      progressPercent = Math.min(100, Math.max(0, Math.round(((nowMs - pdStartMs) / (pdEndMs - pdStartMs)) * 100)));
    }

    pds.push({
      planet,
      startDate: new Date(pdStartMs).toISOString(),
      endDate: new Date(pdEndMs).toISOString(),
      durationDays,
      durationMonths,
      isCurrent,
      daysRemaining,
      progressPercent
    });
  }

  // If no PD was marked isCurrent because nowMs is outside the AD span,
  // we do not force isCurrent = true.
  return pds;
}

/**
 * Calculates classical geometric relationship between MD Lord and AD Lord.
 * Uses ACTUAL natal D1 house placements of the MD and AD planets when provided,
 * preventing abstract planetary sign-index conflation.
 */
export function getMdAdRelationship(
  mdPlanet: PlanetName, 
  adPlanet: PlanetName,
  natalPlanets?: PlanetPosition[]
): DashaHorizonRelationship & { isNatalBased: boolean } {
  if (mdPlanet === adPlanet) {
    return {
      angle: 1,
      labelEn: 'Same House / Lord (1/1)',
      labelHi: 'सम / एकाधिपत्य (१/१)',
      nature: 'neutral',
      isNatalBased: Boolean(natalPlanets)
    };
  }

  let angle = 1;
  let isNatalBased = false;

  if (natalPlanets && Array.isArray(natalPlanets) && natalPlanets.length > 0) {
    const mdPos = natalPlanets.find(p => p.name === mdPlanet);
    const adPos = natalPlanets.find(p => p.name === adPlanet);
    if (mdPos && adPos && mdPos.house && adPos.house) {
      const diff = (adPos.house - mdPos.house + 12) % 12;
      angle = diff === 0 ? 1 : diff + 1;
      isNatalBased = true;
    }
  }

  if (!isNatalBased) {
    // Fallback natural order index (retained for headless legacy compatibility, flagged as non-natal)
    const naturalOrder: Record<PlanetName, number> = {
      Sun: 5, Moon: 4, Mars: 1, Mercury: 3, Jupiter: 9, Venus: 2, Saturn: 10, Rahu: 11, Ketu: 8, Ascendant: 1
    };
    const mdIdx = naturalOrder[mdPlanet] ?? 1;
    const adIdx = naturalOrder[adPlanet] ?? 1;
    const diff = (adIdx - mdIdx + 12) % 12;
    angle = diff === 0 ? 1 : diff + 1;
  }

  if (angle === 1) {
    return {
      angle: 1,
      labelEn: isNatalBased ? 'Same Natal House (1/1)' : 'Same Base (1/1)',
      labelHi: isNatalBased ? 'एक ही भाव में युति (१/१)' : 'एक राशि संबंध (१/१)',
      nature: 'neutral',
      isNatalBased
    };
  } else if (angle === 5 || angle === 9) {
    return {
      angle,
      labelEn: `Trikona (${angle}/9) Auspicious`,
      labelHi: `त्रिकोण (${angle}/९) शुभ समृद्धि`,
      nature: 'harmonious',
      isNatalBased
    };
  } else if (angle === 4 || angle === 7 || angle === 10) {
    return {
      angle,
      labelEn: `Kendra (${angle}/10) Dynamic Action`,
      labelHi: `केंद्र (${angle}/१०) सक्रिय प्रगति`,
      nature: 'harmonious',
      isNatalBased
    };
  } else if (angle === 6 || angle === 8) {
    return {
      angle,
      labelEn: `Shadashtaka (${angle}/8) Challenging`,
      labelHi: `षडाष्टक (${angle}/८) सावधानी व सतर्कता`,
      nature: 'frictional',
      isNatalBased
    };
  } else if (angle === 2 || angle === 12) {
    return {
      angle,
      labelEn: `Dvidvadasa (${angle}/12) High Outflow`,
      labelHi: `द्विर्द्वादश (${angle}/१२) व्यय व परिवर्तन`,
      nature: 'intense',
      isNatalBased
    };
  } else if (angle === 3 || angle === 11) {
    return {
      angle,
      labelEn: `Upachaya (${angle}/11) Growth & Gains`,
      labelHi: `उपचय (${angle}/११) पराक्रम व लाभ`,
      nature: 'harmonious',
      isNatalBased
    };
  }

  return {
    angle,
    labelEn: `House ${angle} Relation`,
    labelHi: `भाव ${angle} संबंध`,
    nature: 'neutral',
    isNatalBased
  };
}

/**
 * Builds a chronological 2–5 year tactical predictive horizon.
 * Seamlessly crosses Mahadasha boundaries and tracks native's age.
 */
export function getMultiYearDashaHorizon(
  dashas: DashaPeriod[],
  birthDateStr?: string,
  options?: {
    maxPeriods?: number;
    minMonthsAhead?: number;
    natalPlanets?: PlanetPosition[];
  }
): DashaHorizonItem[] {
  if (!dashas || dashas.length === 0) return [];

  const maxPeriods = options?.maxPeriods ?? 6;
  const nowMs = Date.now();
  const birthMs = birthDateStr ? new Date(birthDateStr).getTime() : null;

  // 1. Locate current Mahadasha index
  let currentMdIdx = dashas.findIndex(d => {
    const s = new Date(d.startDate).getTime();
    const e = new Date(d.endDate).getTime();
    return nowMs >= s && nowMs <= e;
  });

  if (currentMdIdx === -1) {
    currentMdIdx = dashas.findIndex(d => d.isCurrent);
  }
  if (currentMdIdx === -1) currentMdIdx = 0;

  // 2. Flatten upcoming Antardashas starting from the current AD
  const horizon: DashaHorizonItem[] = [];
  let foundCurrent = false;

  for (let m = currentMdIdx; m < dashas.length; m++) {
    const md = dashas[m];
    const adList = md.antardashas || [];

    for (let a = 0; a < adList.length; a++) {
      const ad = adList[a];
      const adStartMs = new Date(ad.startDate).getTime();
      const adEndMs = new Date(ad.endDate).getTime();

      const isCurrentAd = (nowMs >= adStartMs && nowMs <= adEndMs) || ad.isCurrent;

      // Start collecting from current AD (or immediate predecessor if requested)
      if (!foundCurrent) {
        if (isCurrentAd || adEndMs >= nowMs) {
          foundCurrent = true;
        } else {
          continue;
        }
      }

      // Check if this is the last AD of an MD (Dasha Chidra)
      const isDashaChidra = a === adList.length - 1;

      // Check if this represents an Era Shift (first AD of a new MD)
      const isEraShift = a === 0 && m > currentMdIdx;
      const prevMdPlanet = isEraShift && m > 0 ? dashas[m - 1].planet : undefined;

      // Age calculation
      let ageStart: number | undefined;
      let ageEnd: number | undefined;
      if (birthMs && !isNaN(birthMs)) {
        ageStart = Number(((adStartMs - birthMs) / (1000 * 60 * 60 * 24 * 365.25)).toFixed(1));
        ageEnd = Number(((adEndMs - birthMs) / (1000 * 60 * 60 * 24 * 365.25)).toFixed(1));
      }

      // Days remaining & progress
      const daysRemaining = Math.max(0, Math.ceil((adEndMs - nowMs) / (1000 * 60 * 60 * 24)));
      let progressPercent = 0;
      if (nowMs > adEndMs) {
        progressPercent = 100;
      } else if (nowMs >= adStartMs) {
        progressPercent = Math.min(100, Math.max(0, Math.round(((nowMs - adStartMs) / (adEndMs - adStartMs)) * 100)));
      }

      const relationshipWithMd = getMdAdRelationship(md.planet, ad.planet, options?.natalPlanets);
      const pratyantardashas = calculatePratyantardashas(ad.planet, ad.startDate, ad.endDate);

      horizon.push({
        id: `${md.planet}-${ad.planet}-${ad.startDate}`,
        mdPlanet: md.planet,
        adPlanet: ad.planet,
        startDate: ad.startDate,
        endDate: ad.endDate,
        durationYears: ad.durationYears,
        isCurrent: isCurrentAd,
        daysRemaining,
        progressPercent,
        isDashaChidra,
        isEraShift,
        prevMdPlanet,
        ageStart,
        ageEnd,
        relationshipWithMd,
        pratyantardashas
      });

      if (horizon.length >= maxPeriods) break;
    }

    if (horizon.length >= maxPeriods) break;
  }

  return horizon;
}

/**
 * Format date helpers for Astrological Cockpit
 */
export function formatDashaDateYear(isoDate: string): string {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  return d.getFullYear().toString();
}

export function formatDashaMonthYear(isoDate: string): string {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getMonth()]} ${d.getFullYear()}`;
}

export function formatDashaDayMonth(isoDate: string): string {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d.getDate()} ${months[d.getMonth()]}`;
}

export function formatDashaFullDate(isoDate: string): string {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

export interface SubDashaItem {
  planet: PlanetName;
  startDate: string;
  endDate: string;
  durationHours?: number;
  durationDays?: number;
  isCurrent: boolean;
  progressPercent: number;
}

/**
 * Calculates Sookshma Dashas (Level 4) inside a Pratyantardasha window.
 */
export function calculateSookshmaDashas(
  pdPlanet: PlanetName,
  pdStartDate: string | Date,
  pdEndDate: string | Date
): SubDashaItem[] {
  const startMs = new Date(pdStartDate).getTime();
  const endMs = new Date(pdEndDate).getTime();
  const totalMs = Math.max(0, endMs - startMs);

  const startIdx = VIMSHOTTARI_ORDER.indexOf(pdPlanet);
  const safeStartIdx = startIdx >= 0 ? startIdx : 0;
  const nowMs = Date.now();
  let runningMs = startMs;
  const sds: SubDashaItem[] = [];

  for (let i = 0; i < 9; i++) {
    const planet = VIMSHOTTARI_ORDER[(safeStartIdx + i) % 9];
    const planetYears = VIMSHOTTARI_YEARS[planet] || 7;
    const sdMs = Math.round((planetYears / 120) * totalMs);
    const sdStartMs = runningMs;
    const sdEndMs = i === 8 ? endMs : Math.min(endMs, sdStartMs + sdMs);
    runningMs = sdEndMs;

    const isCurrent = nowMs >= sdStartMs && nowMs <= sdEndMs;
    const durationHours = Math.max(1, Math.round((sdEndMs - sdStartMs) / (1000 * 60 * 60)));
    const durationDays = Number((durationHours / 24).toFixed(1));

    let progressPercent = 0;
    if (nowMs > sdEndMs) progressPercent = 100;
    else if (nowMs >= sdStartMs && sdEndMs > sdStartMs) {
      progressPercent = Math.min(100, Math.max(0, Math.round(((nowMs - sdStartMs) / (sdEndMs - sdStartMs)) * 100)));
    }

    sds.push({
      planet,
      startDate: new Date(sdStartMs).toISOString(),
      endDate: new Date(sdEndMs).toISOString(),
      durationHours,
      durationDays,
      isCurrent,
      progressPercent
    });
  }

  return sds;
}

/**
 * Calculates Prana Dashas (Level 5) inside a Sookshma Dasha window.
 */
export function calculatePranaDashas(
  sdPlanet: PlanetName,
  sdStartDate: string | Date,
  sdEndDate: string | Date
): SubDashaItem[] {
  const startMs = new Date(sdStartDate).getTime();
  const endMs = new Date(sdEndDate).getTime();
  const totalMs = Math.max(0, endMs - startMs);

  const startIdx = VIMSHOTTARI_ORDER.indexOf(sdPlanet);
  const safeStartIdx = startIdx >= 0 ? startIdx : 0;
  const nowMs = Date.now();
  let runningMs = startMs;
  const prs: SubDashaItem[] = [];

  for (let i = 0; i < 9; i++) {
    const planet = VIMSHOTTARI_ORDER[(safeStartIdx + i) % 9];
    const planetYears = VIMSHOTTARI_YEARS[planet] || 7;
    const prMs = Math.round((planetYears / 120) * totalMs);
    const prStartMs = runningMs;
    const prEndMs = i === 8 ? endMs : Math.min(endMs, prStartMs + prMs);
    runningMs = prEndMs;

    const isCurrent = nowMs >= prStartMs && nowMs <= prEndMs;
    const durationHours = Math.max(0.1, Number(((prEndMs - prStartMs) / (1000 * 60 * 60)).toFixed(1)));

    let progressPercent = 0;
    if (nowMs > prEndMs) progressPercent = 100;
    else if (nowMs >= prStartMs && prEndMs > prStartMs) {
      progressPercent = Math.min(100, Math.max(0, Math.round(((nowMs - prStartMs) / (prEndMs - prStartMs)) * 100)));
    }

    prs.push({
      planet,
      startDate: new Date(prStartMs).toISOString(),
      endDate: new Date(prEndMs).toISOString(),
      durationHours,
      isCurrent,
      progressPercent
    });
  }

  return prs;
}

export interface Active5LevelDashaPath {
  md: { planet: PlanetName; startDate: string; endDate: string; progressPercent: number };
  ad: { planet: PlanetName; startDate: string; endDate: string; progressPercent: number; daysRemaining: number };
  pd: { planet: PlanetName; startDate: string; endDate: string; progressPercent: number; daysRemaining: number };
  sd: { planet: PlanetName; startDate: string; endDate: string; progressPercent: number; hoursRemaining: number };
  prd: { planet: PlanetName; startDate: string; endDate: string; progressPercent: number; hoursRemaining: number };
}

/**
 * Resolves the complete 5-level active hierarchy along the active node branch.
 */
export function getActive5LevelDashaPath(dashas: DashaPeriod[]): Active5LevelDashaPath | null {
  if (!dashas || dashas.length === 0) return null;
  const nowMs = Date.now();

  const md = dashas.find(d => {
    const s = new Date(d.startDate).getTime();
    const e = new Date(d.endDate).getTime();
    return nowMs >= s && nowMs <= e;
  }) || dashas.find(d => d.isCurrent) || dashas[0];

  if (!md) return null;
  const mdStart = new Date(md.startDate).getTime();
  const mdEnd = new Date(md.endDate).getTime();
  const mdProgress = mdEnd > mdStart ? Math.min(100, Math.max(0, Math.round(((nowMs - mdStart) / (mdEnd - mdStart)) * 100))) : 50;

  const adList = md.antardashas || [];
  const ad = adList.find(a => {
    const s = new Date(a.startDate).getTime();
    const e = new Date(a.endDate).getTime();
    return nowMs >= s && nowMs <= e;
  }) || adList.find(a => a.isCurrent) || adList[0];

  if (!ad) return null;
  const adStart = new Date(ad.startDate).getTime();
  const adEnd = new Date(ad.endDate).getTime();
  const adProgress = adEnd > adStart ? Math.min(100, Math.max(0, Math.round(((nowMs - adStart) / (adEnd - adStart)) * 100))) : 50;
  const adDaysRemaining = Math.max(0, Math.ceil((adEnd - nowMs) / (1000 * 60 * 60 * 24)));

  const pds = calculatePratyantardashas(ad.planet, ad.startDate, ad.endDate);
  const pd = pds.find(p => p.isCurrent) || pds[0];
  const pdEnd = new Date(pd.endDate).getTime();
  const pdDaysRemaining = Math.max(0, Math.ceil((pdEnd - nowMs) / (1000 * 60 * 60 * 24)));

  const sds = calculateSookshmaDashas(pd.planet, pd.startDate, pd.endDate);
  const sd = sds.find(s => s.isCurrent) || sds[0];
  const sdEnd = new Date(sd.endDate).getTime();
  const sdHoursRemaining = Math.max(0, Math.ceil((sdEnd - nowMs) / (1000 * 60 * 60)));

  const prds = calculatePranaDashas(sd.planet, sd.startDate, sd.endDate);
  const prd = prds.find(p => p.isCurrent) || prds[0];
  const prdEnd = new Date(prd.endDate).getTime();
  const prdHoursRemaining = Math.max(0, Number(((prdEnd - nowMs) / (1000 * 60 * 60)).toFixed(1)));

  return {
    md: { planet: md.planet, startDate: md.startDate, endDate: md.endDate, progressPercent: mdProgress },
    ad: { planet: ad.planet, startDate: ad.startDate, endDate: ad.endDate, progressPercent: adProgress, daysRemaining: adDaysRemaining },
    pd: { planet: pd.planet, startDate: pd.startDate, endDate: pd.endDate, progressPercent: pd.progressPercent, daysRemaining: pdDaysRemaining },
    sd: { planet: sd.planet, startDate: sd.startDate, endDate: sd.endDate, progressPercent: sd.progressPercent, hoursRemaining: sdHoursRemaining },
    prd: { planet: prd.planet, startDate: prd.startDate, endDate: prd.endDate, progressPercent: prd.progressPercent, hoursRemaining: prdHoursRemaining }
  };
}
